import { useState, useEffect } from 'react';
import { Search, Filter, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, BookOpen, User } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { adminApi } from '@/services/adminApi';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface Student {
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  gradeLevel: number | null;
  personalityType: string | null;
  personalityCompletedAt: string | null;
  lastActivityAt: string | null;
  createdAt: string;
  parent: {
    parentId: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  progress: {
    completedLessons: number;
    totalLessons: number;
    percentComplete: number;
    totalTimeMinutes: number;
    completedTests: number;
    totalTests: number;
    averageTestScore: number | null;
  };
}

type SortColumn = 'studentName' | 'parentName' | 'role' | 'personality' | 'lessons' | 'progress' | 'tests' | 'averageScore' | 'timeSpent' | 'lastActive' | 'createdAt';
type SortOrder = 'asc' | 'desc';

export function StudentsPage() {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState<SortColumn>('createdAt');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const limit = 20;

  useEffect(() => {
    loadStudents();
  }, [page, sortBy, sortOrder, roleFilter]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await adminApi.getStudents({
        page,
        limit,
        sortBy,
        sortOrder,
        role: roleFilter || undefined,
      });
      setStudents(data.students);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ column }: { column: SortColumn }) => {
    if (sortBy !== column) {
      return <ChevronUp className="w-4 h-4 text-muted-foreground/30" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="w-4 h-4" />
      : <ChevronDown className="w-4 h-4" />;
  };

  const filteredStudents = students.filter((student) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      student.firstName.toLowerCase().includes(search) ||
      student.lastName.toLowerCase().includes(search) ||
      student.email.toLowerCase().includes(search) ||
      (student.parent?.firstName.toLowerCase().includes(search)) ||
      (student.parent?.lastName.toLowerCase().includes(search))
    );
  });

  const getProgressColor = (percent: number) => {
    if (percent === 100) return 'bg-green-500';
    if (percent >= 50) return 'bg-blue-500';
    if (percent > 0) return 'bg-yellow-500';
    return 'bg-gray-300';
  };

  const formatTime = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Student Progress</h1>
        <p className="text-muted-foreground mt-1">
          View all students, their lesson progress, and parent associations
        </p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by student or parent name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              className="form-input w-36"
            >
              <option value="">All Types</option>
              <option value="Student">Teens</option>
              <option value="AdultLearner">Adults</option>
            </select>
            <Button variant="outline" onClick={() => loadStudents()}>
              <Filter className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            Students ({total})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner message="Loading students..." />
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-destructive mb-4">{error}</p>
              <Button onClick={loadStudents}>Retry</Button>
            </div>
          ) : filteredStudents.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">
              No students found
            </p>
          ) : (
            <>
              <div className="table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('studentName')}
                      >
                        <div className="flex items-center gap-1">
                          Student
                          <SortIcon column="studentName" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('role')}
                      >
                        <div className="flex items-center gap-1">
                          Type
                          <SortIcon column="role" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('parentName')}
                      >
                        <div className="flex items-center gap-1">
                          Parent
                          <SortIcon column="parentName" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('personality')}
                      >
                        <div className="flex items-center gap-1">
                          Personality
                          <SortIcon column="personality" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('lessons')}
                      >
                        <div className="flex items-center gap-1">
                          Lessons
                          <SortIcon column="lessons" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('progress')}
                      >
                        <div className="flex items-center gap-1">
                          Progress
                          <SortIcon column="progress" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('tests')}
                      >
                        <div className="flex items-center gap-1">
                          Tests
                          <SortIcon column="tests" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('averageScore')}
                      >
                        <div className="flex items-center gap-1">
                          Avg Grade
                          <SortIcon column="averageScore" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('timeSpent')}
                      >
                        <div className="flex items-center gap-1">
                          Time Spent
                          <SortIcon column="timeSpent" />
                        </div>
                      </th>
                      <th
                        className="cursor-pointer select-none hover:bg-muted/50"
                        onClick={() => handleSort('lastActive')}
                      >
                        <div className="flex items-center gap-1">
                          Last Active
                          <SortIcon column="lastActive" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStudents.map((student) => (
                      <tr key={student.studentId}>
                        <td>
                          <div>
                            <p className="font-medium">
                              {student.firstName} {student.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {student.email}
                            </p>
                          </div>
                        </td>
                        <td>
                          <Badge variant={student.role === 'AdultLearner' ? 'outline' : 'default'}>
                            {student.role === 'AdultLearner' ? 'Adult' : 'Teen'}
                          </Badge>
                        </td>
                        <td>
                          {student.parent ? (
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p className="text-sm">
                                  {student.parent.firstName} {student.parent.lastName}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {student.parent.email}
                                </p>
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td>
                          {student.personalityType ? (
                            <Badge variant="outline" className="capitalize">
                              {student.personalityType}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-sm">Not taken</span>
                          )}
                        </td>
                        <td>
                          <span className="font-medium">
                            {student.progress.completedLessons}
                          </span>
                          <span className="text-muted-foreground">
                            /{student.progress.totalLessons}
                          </span>
                        </td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${getProgressColor(student.progress.percentComplete)}`}
                                style={{ width: `${student.progress.percentComplete}%` }}
                              />
                            </div>
                            <span className="text-sm text-muted-foreground w-10">
                              {student.progress.percentComplete}%
                            </span>
                          </div>
                        </td>
                        <td>
                          <span className="font-medium">
                            {student.progress.completedTests}
                          </span>
                          <span className="text-muted-foreground">
                            /{student.progress.totalTests}
                          </span>
                        </td>
                        <td>
                          {student.progress.averageTestScore != null ? (
                            <span className="font-medium">{student.progress.averageTestScore}%</span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </td>
                        <td className="text-muted-foreground">
                          {formatTime(student.progress.totalTimeMinutes)}
                        </td>
                        <td className="text-muted-foreground text-sm">
                          {student.lastActivityAt
                            ? new Date(student.lastActivityAt).toLocaleDateString()
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="px-3 py-1 text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
